import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightFromBracket,
  faUser,
  faUserGroup,
  faEyeSlash,
  faEye,
  faDollarSign,
  faGlobe,
  faVectorSquare,
} from "@fortawesome/free-solid-svg-icons";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { Switch } from "../../../components/ui/Switch";
import { Link } from "react-router-dom";
import { NavigationBarButton } from "./NavigationBarButton";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { BaseMenu } from "../../../components/Cart/Cart-Icon/BaseMenu";
import { useAdminImpersonationDialog } from "../hooks/useAdminImpersonationDialog";
import { useOrganizationImpersonationContext } from "../../../../contexts/OrganizationImpersonationContext";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { useWorkshopMode } from "../../../../hooks/useWorkshopMode";
import {
  setLanguage,
  AppLanguage,
  SUPPORTED_LANGUAGES,
  isAppLanguage,
} from "../../../../i18n";
import { enviroment } from "../../../../enviroment";
import { useSyncExternalStore } from "react";
import {
  isActAsFrameEnabled,
  setActAsFrameEnabled,
  subscribeActAsFrame,
} from "../../../../contexts/actAsFrameStore";

interface LoggedInMenu {
  logout: () => void;
}

export function LoggedInMenu({ logout }: LoggedInMenu) {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { profile, role } = useProfileContext();
  const {
    hidePrices,
    showCustomerPrices,
    togglePriceVisibility,
    toggleCustomerPrices,
  } = usePriceVisibility();
  const cartHidden = useCartHidden();
  const workshopMode = useWorkshopMode();
  const hasPriceIndicators =
    !cartHidden &&
    (hidePrices || (role === Role.Vendor && showCustomerPrices));
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  const { isImpersonatingAdmin } = useProfileContext();
  // After a hard reload while impersonating, the sticky admin flag is false
  // (first profile fetched is the vendor's) — active impersonation state must
  // still surface the menu entry so the admin can switch vendor or reset.
  const { organizationId: impersonatedOrgId } =
    useOrganizationImpersonationContext();

  const { component, open: openImpersonation } = useAdminImpersonationDialog({
    onContinue: async () => {},
  });

  const actAsFrameEnabled = useSyncExternalStore(
    subscribeActAsFrame,
    isActAsFrameEnabled
  );

  function toggleActAsFrame() {
    setActAsFrameEnabled(!actAsFrameEnabled);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleLogOut() {
    handleClose();
    logout();
  }

  const languageBase = i18n.language?.split("-")[0];
  const currentLanguage: AppLanguage = isAppLanguage(languageBase)
    ? languageBase
    : "en";

  function handleLanguageChange(language: AppLanguage) {
    if (language !== currentLanguage) {
      setLanguage(language);
    }
  }

  function getUserInfo() {
    switch (role!) {
      case Role.Admin:
        return t("headerFooter.jetsportAdmin");
      case Role.Vendor:
        return /* profile?.userOrganizationInformations.organizationName ?? */ t(
          "headerFooter.profile"
        );
      case Role.EndUser:
        return /* profile?.userOrganizationInformations.organizationName ?? */ t(
          "headerFooter.profile"
        );
    }
    // role undefined = profile still in flight (e.g. act-as-vendor org switch).
    // Never fall through to logout() DURING RENDER — show a neutral label.
    if (role === undefined) {
      return t("headerFooter.profile");
    }
    handleClose();
    logout();
  }

  return (
    <div className={"flex items-center h-full"}>
      {component}
      {workshopMode && (
        <div
          className={
            "flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-3 py-1 text-xs font-semibold uppercase whitespace-nowrap"
          }
        >
          <FontAwesomeIcon icon={faEyeSlash} />
          <span>{t("headerFooter.workshopMode")}</span>
        </div>
      )}
      <NavigationBarButton
        className={"!w-[60px] relative"}
        onClick={handleClick}
      >
        {hasPriceIndicators && (
          <div
            className={
              "h-[7px] w-[7px] rounded-full bg-success absolute top-5 right-5"
            }
          />
        )}
        {<FontAwesomeIcon size={"lg"} icon={faUser} />}
      </NavigationBarButton>
      <BaseMenu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Link to={"/account/me"}>
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faUser} />
            </ListItemIcon>
            <ListItemText>{getUserInfo()}</ListItemText>
          </MenuItem>
        </Link>
        {!cartHidden && (
          <MenuItem onClick={togglePriceVisibility}>
            <ListItemIcon>
              <FontAwesomeIcon icon={hidePrices ? faEyeSlash : faEye} />
            </ListItemIcon>
            <ListItemText className="mr-4">
              {t("headerFooter.hidePrices")}
            </ListItemText>
            <Switch
              checked={hidePrices}
              onCheckedChange={togglePriceVisibility}
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        {(role === Role.Vendor || role === Role.Admin) && !workshopMode && (
          <MenuItem onClick={toggleCustomerPrices}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faDollarSign} />
            </ListItemIcon>
            <ListItemText className="mr-4">
              {t("headerFooter.showCustomerPrices")}
            </ListItemText>
            <Switch
              checked={showCustomerPrices}
              onCheckedChange={toggleCustomerPrices}
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}

        {enviroment.actAsVendorEnabled &&
          (isImpersonatingAdmin || impersonatedOrgId != null) && (
          <MenuItem
            onClick={() => {
              handleClose();
              openImpersonation();
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faUserGroup} />
            </ListItemIcon>
            <ListItemText>{t("headerFooter.actAsVendor")}</ListItemText>
          </MenuItem>
        )}
        {enviroment.actAsVendorEnabled &&
          (isImpersonatingAdmin || impersonatedOrgId != null) && (
          <MenuItem onClick={toggleActAsFrame}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faVectorSquare} />
            </ListItemIcon>
            <ListItemText className="mr-4">
              {t("headerFooter.actAsFrame")}
            </ListItemText>
            <Switch
              checked={actAsFrameEnabled}
              onCheckedChange={toggleActAsFrame}
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => e.stopPropagation()}
          className="!flex-col !items-start"
        >
          <div className="flex items-center">
            <ListItemIcon>
              <FontAwesomeIcon icon={faGlobe} />
            </ListItemIcon>
            <ListItemText className="mr-4">
              {t("headerFooter.language")}
            </ListItemText>
          </div>
          <div className="grid grid-cols-2 gap-1 mt-2 w-full">
            {SUPPORTED_LANGUAGES.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageChange(code);
                }}
                className={`rounded px-2 py-0.5 text-xs font-semibold text-left ${
                  currentLanguage === code
                    ? "bg-gray-800 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </MenuItem>
        <MenuItem onClick={handleLogOut}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faRightFromBracket} />
          </ListItemIcon>
          <ListItemText>{t("logout")}</ListItemText>
        </MenuItem>
      </BaseMenu>
    </div>
  );
}
